import { useState } from 'react';

interface DailyRow {
  date: string;
  clicksTotal: number;
  clicksQuality: number;
  clicksBot: number;
  conversions: number;
  revenue: number;
}

interface HourlyRow {
  hour: number;
  conversionsApproved: number;
  revenueGross: number;
}

interface PerformanceChartProps {
  daily: DailyRow[];
  hourly?: HourlyRow[];
  isToday: boolean;
}

type Metric = 'clicks' | 'conversions' | 'revenue';

export function PerformanceChart({
  daily,
  hourly,
  isToday,
}: PerformanceChartProps) {
  const [metric, setMetric] = useState<Metric>('clicks');

  // Prepare data: use hourly for today, daily otherwise
  const rows = isToday && hourly
    ? hourly.map((h, i) => ({
        label: `${h.hour.toString().padStart(2, '0')}:00`,
        clicks: 0,
        conversions: h.conversionsApproved,
        revenue: h.revenueGross,
      }))
    : daily.map((d) => ({
        label: d.date,
        clicksTotal: d.clicksTotal,
        clicksQuality: d.clicksQuality,
        clicksBot: d.clicksBot,
        conversions: d.conversions,
        revenue: d.revenue,
      }));

  // Normalize based on selected metric
  const max =
    metric === 'clicks'
      ? Math.max(1, ...rows.map((r) => (r as any).clicksTotal ?? 0))
      : metric === 'conversions'
      ? Math.max(1, ...rows.map((r) => r.conversions))
      : Math.max(0.0001, ...rows.map((r) => r.revenue));

  const heightPct = (v: number) => ((v / max) * 100).toFixed(2);

  return (
    <div>
      {/* Metric toggle pills */}
      <div className="flex items-center gap-2 mb-3">
        {(['clicks', 'conversions', 'revenue'] as Metric[]).map((m) => (
          <button
            key={m}
            onClick={() => setMetric(m)}
            className={`px-3 py-1.5 text-[10px] font-display uppercase tracking-wider rounded border transition-all ${
              metric === m
                ? 'bg-neon-magenta/20 border-neon-magenta text-neon-magenta'
                : 'bg-bg-tertiary border-border-subtle text-text-secondary hover:border-neon-magenta/40'
            }`}
          >
            {m === 'clicks' ? 'Klik' : m === 'conversions' ? 'Konversi' : 'Revenue'}
          </button>
        ))}
      </div>

      {/* Chart container */}
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded min-h-[160px] flex flex-col justify-end">
        {rows.length === 0 ? (
          <p className="text-[11px] font-mono text-text-tertiary italic text-center py-4">
            no data in this range
          </p>
        ) : (
          <>
            {metric === 'clicks' ? (
              /* Bars for clicks */
              <div className="flex items-end gap-1 h-32 px-1 border-b border-border-subtle/40 pb-px">
                {rows.map((r, i) => {
                  const v = (r as any).clicksTotal ?? 0;
                  const h = heightPct(v);
                  return (
                    <div
                      key={i}
                      className="flex-1 h-full relative group"
                      title={`${r.label}: ${v} clicks`}
                    >
                      {/* Tooltip CSS pure */}
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-bg-secondary border border-neon-cyan/40 rounded px-2 py-1 text-[9px] font-mono whitespace-nowrap z-10 pointer-events-none">
                        <div className="text-text-primary">{r.label}</div>
                        <div className="text-neon-cyan">{v} klik</div>
                      </div>
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-neon-cyan/40 rounded-t-sm transition-all group-hover:bg-neon-cyan/60"
                        style={{ height: `${h}%`, minHeight: v > 0 ? '2px' : '0' }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : metric === 'conversions' ? (
              /* Thin bars for conversions */
              <div className="flex items-end gap-1 h-32 px-1 border-b border-border-subtle/40 pb-px">
                {rows.map((r, i) => {
                  const h = heightPct(r.conversions);
                  return (
                    <div
                      key={i}
                      className="flex-1 h-full relative group"
                      title={`${r.label}: ${r.conversions} konversi`}
                    >
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-bg-secondary border border-neon-magenta/40 rounded px-2 py-1 text-[9px] font-mono whitespace-nowrap z-10 pointer-events-none">
                        <div className="text-text-primary">{r.label}</div>
                        <div className="text-neon-magenta">{r.conversions} konversi</div>
                      </div>
                      <div
                        className="absolute bottom-0 left-1/4 right-1/4 bg-neon-magenta/80 rounded-t-sm"
                        style={{ height: `${h}%`, minHeight: r.conversions > 0 ? '2px' : '0' }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Polyline + area for revenue */
              <RevenueArea rows={rows} max={max} />
            )}

            {/* X-axis labels: first/mid/last */}
            <div className="flex justify-between text-[9px] font-mono text-text-tertiary mt-1 px-1">
              <span>{rows[0]?.label ?? ''}</span>
              {rows.length > 2 && (
                <span>{rows[Math.floor(rows.length / 2)]?.label ?? ''}</span>
              )}
              <span>{rows[rows.length - 1]?.label ?? ''}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Revenue polyline + area chart with fill */
function RevenueArea({
  rows,
  max,
}: {
  rows: Array<{ label: string; revenue: number }>;
  max: number;
}) {
  // Build path coordinates
  const stepX = 100 / Math.max(1, rows.length - 1);
  const points = rows.map((r, i) => {
    const x = i * stepX;
    const y = 100 - ((r.revenue / max) * 100);
    return `${x},${y}`;
  });
  const polyline = points.join(' ');
  const polygon = `${polyline} 100,100 0,100`;

  return (
    <div className="h-32 px-1 border-b border-border-subtle/40 relative overflow-visible">
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Area fill */}
        <polygon
          points={polygon}
          className="fill-neon-yellow/20"
        />
        {/* Polyline */}
        <polyline
          points={polyline}
          fill="none"
          stroke="#fde047"
          strokeWidth="2"
          className="drop-shadow-md"
        />
        {/* Dots at each point */}
        {points.map((p, i) => {
          const [x, y] = p.split(',').map(Number);
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="3"
              className="fill-neon-yellow stroke-bg-tertiary stroke-[1]"
            />
          );
        })}
      </svg>

      {/* Tooltips on hover per point */}
      {rows.map((r, i) => {
        const x = i * stepX;
        return (
          <div
            key={i}
            className="absolute bottom-full mb-1 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            style={{ left: `${x}%` }}
          >
            <div className="bg-bg-secondary border border-neon-yellow/40 rounded px-2 py-1 text-[9px] font-mono whitespace-nowrap shadow-lg">
              <div className="text-text-primary">{r.label}</div>
              <div className="text-neon-yellow">{formatCurrency(r.revenue)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
